/**
 * Pure helpers for the tools authoring surfaces.
 *
 * Extracted from the 5 tool route files during PR-C-MINIMAL
 * (2026-05-12). See the sibling `tools-shared.constants.ts` header
 * for context.
 *
 * All exports are pure, side-effect-free, and have zero React /
 * Sonner / fetch dependencies. Consumers wrap them with toast calls
 * or `setFormError` updates as appropriate to their UX.
 */

import { z } from 'zod';

// ─── Tool Helper draft + mutation → patch (#1092 fix) ───────────────────────

/**
 * The in-form draft shape consumed by `tools.new.tsx`. Mirrors the
 * `useState` setters in that file. Kept here (rather than imported
 * from the route module) so the helper stays pure / testable without
 * pulling in the Card / RadioGroup tree.
 *
 * Field names match the form's setter names exactly (`setName`,
 * `setDisplayName`, …) so the caller can spread the patch into a
 * single functional setState. See `applyToolHelperMutation` below for
 * the design rationale.
 */
export interface ToolHelperDraft {
  name: string;
  displayName: string;
  description: string;
  category: string;
  toolType: 'http' | 'database';
  httpMethod: string;
  httpUrl: string;
  /**
   * Path + query template appended to httpUrl (baseUrl) at runtime, with
   * `{{param}}` placeholders substituted from the call args. REQUIRED for a
   * parameterized GET/DELETE (those send no body), and read by the runtime HTTP
   * adapter from `source_config.pathTemplate`. Placeholder names must match the
   * parameter names. e.g. `/v1/forecast?latitude={{lat}}&longitude={{lon}}`.
   */
  httpPathTemplate: string;
  httpHeaders: Array<{ key: string; value: string }>;
  httpBody: string;
  outputSchemaJson: string;
  sqlQuery: string;
  dbConnection: string;
  sideEffects: string[];
  policy: 'auto_approve' | 'require_approval' | 'forbidden';
  parameters: Array<{
    name: string;
    /**
     * Aligned with the tenant form's `ToolParameter.type` union in
     * `routes/studio/tools.new.tsx`. The form's Select widget renders
     * `string | number | boolean | object` (the `array` row in the
     * detail page's parameters editor is a v1.1 follow-up). We coerce
     * incoming `array` schemas to `object` so the helper never blocks
     * Accept on a type the form can't render.
     */
    type: 'string' | 'number' | 'boolean' | 'object';
    required: boolean;
    description: string;
  }>;
}

/**
 * Slug-safe sanitiser shared with the form's onChange handler. The
 * Tool Helper LLM can emit a `slug` arg that contains arbitrary
 * characters (e.g. "weather.get_current") but the tenant form's
 * canonical sanitiser strips anything outside `[a-z0-9_]` (matches
 * the inline `onChange` rewrite in `tools.new.tsx` ≈ L313).
 *
 * Exported so tests can assert the parity directly.
 */
export function sanitizeToolSlug(input: string): string {
  return String(input)
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Coerce the loose parameter shape emitted by the Tool Helper LLM via
 * `set_input_schema` into the tenant form's row shape. The LLM emits
 * a JSON-string blob representing a JSON Schema; we parse it and walk
 * `properties`. Unknown / unsupported types fall back to `string`.
 *
 * Returns `null` when the schemaJson is unparseable or not an object
 * schema — the caller should leave parameters untouched.
 */
export function parseToolInputSchemaForHelper(
  schemaJson: unknown,
): ToolHelperDraft['parameters'] | null {
  if (typeof schemaJson !== 'string') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(schemaJson);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const schema = parsed as {
    properties?: Record<string, { type?: unknown; description?: unknown }>;
    required?: unknown;
  };
  if (!schema.properties || typeof schema.properties !== 'object') return null;
  const requiredSet = new Set(
    Array.isArray(schema.required)
      ? schema.required.filter((v): v is string => typeof v === 'string')
      : [],
  );
  const rows: ToolHelperDraft['parameters'] = [];
  for (const [paramName, raw] of Object.entries(schema.properties)) {
    const desc = raw && typeof (raw as { description?: unknown }).description === 'string'
      ? (raw as { description: string }).description
      : '';
    const typeRaw = raw && typeof (raw as { type?: unknown }).type === 'string'
      ? (raw as { type: string }).type
      : 'string';
    const type: ToolHelperDraft['parameters'][number]['type'] =
      typeRaw === 'number' || typeRaw === 'integer'
        ? 'number'
        : typeRaw === 'boolean'
          ? 'boolean'
          : typeRaw === 'object' || typeRaw === 'array'
            ? 'object'
            : 'string';
    rows.push({
      name: paramName,
      type,
      required: requiredSet.has(paramName),
      description: desc,
    });
  }
  return rows;
}

export function mergeToolParameters(
  existing: ToolHelperDraft['parameters'],
  incoming: ToolHelperDraft['parameters'],
): ToolHelperDraft['parameters'] {
  if (existing.length === 0) return incoming;
  if (incoming.length === 0) return existing;

  const next = [...existing];
  const indexByName = new Map<string, number>();
  for (const [index, param] of next.entries()) {
    const name = param.name.trim().toLowerCase();
    if (name) indexByName.set(name, index);
  }

  for (const param of incoming) {
    const name = param.name.trim().toLowerCase();
    const existingIndex = name ? indexByName.get(name) : undefined;
    if (existingIndex !== undefined) {
      next[existingIndex] = param;
    } else {
      if (name) indexByName.set(name, next.length);
      next.push(param);
    }
  }

  return next;
}

function parseHelperHeaders(value: unknown): ToolHelperDraft['httpHeaders'] {
  if (!Array.isArray(value)) return [];
  const rows: ToolHelperDraft['httpHeaders'] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const key = (raw as { key?: unknown }).key;
    const headerValue = (raw as { value?: unknown }).value;
    if (typeof key !== 'string' || !key.trim()) continue;
    rows.push({
      key: key.trim(),
      value: headerValue == null ? '' : String(headerValue),
    });
  }
  return rows;
}

function authHeaderFromHelperAuth(auth: unknown): ToolHelperDraft['httpHeaders'] {
  if (!auth || typeof auth !== 'object') return [];
  const a = auth as {
    type?: unknown;
    headerName?: unknown;
    envVarName?: unknown;
  };
  const type = typeof a.type === 'string' ? a.type : 'none';
  const envVarName = typeof a.envVarName === 'string' ? a.envVarName.trim() : '';
  if (!envVarName) return [];
  if (type === 'bearer') {
    return [{ key: 'Authorization', value: `Bearer {{${envVarName}}}` }];
  }
  if (type === 'api_key') {
    const headerName =
      typeof a.headerName === 'string' && a.headerName.trim()
        ? a.headerName.trim()
        : 'X-API-Key';
    return [{ key: headerName, value: `{{${envVarName}}}` }];
  }
  if (type === 'basic') {
    return [{ key: 'Authorization', value: `Basic {{${envVarName}}}` }];
  }
  return [];
}

function parseHelperSideEffects(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const allowed = new Set(['read', 'write', 'network', 'fs']);
  const next = Array.from(
    new Set(
      value
        .filter((item): item is string => typeof item === 'string')
        .map((item) => item.trim())
        .filter((item) => allowed.has(item)),
    ),
  );
  return next.length > 0 ? next : undefined;
}

function applyHttpManifestArgs(
  args: Record<string, unknown>,
): Partial<ToolHelperDraft> {
  const patch: Partial<ToolHelperDraft> = {
    toolType: 'http',
  };
  const method = args.method;
  if (
    method === 'GET' ||
    method === 'POST' ||
    method === 'PUT' ||
    method === 'PATCH' ||
    method === 'DELETE'
  ) {
    patch.httpMethod = method;
  }
  const url = args.url ?? args.baseUrl ?? args.executorRef;
  if (url != null) patch.httpUrl = String(url);
  // pathTemplate carries the {{param}} path/query the runtime adapter needs for
  // a parameterized GET/DELETE — must survive to handler_config.pathTemplate.
  if (args.pathTemplate != null) patch.httpPathTemplate = String(args.pathTemplate);
  const headers = [
    ...parseHelperHeaders(args.headers),
    ...authHeaderFromHelperAuth(args.auth),
  ];
  if (headers.length > 0) patch.httpHeaders = headers;
  if (args.bodyTemplate != null) patch.httpBody = String(args.bodyTemplate);
  if (args.category != null) patch.category = String(args.category);
  if (args.inputSchemaJson != null) {
    const parsed = parseToolInputSchemaForHelper(args.inputSchemaJson);
    if (parsed && parsed.length > 0) patch.parameters = parsed;
  }
  if (args.outputSchemaJson != null) patch.outputSchemaJson = String(args.outputSchemaJson);
  const approval = args.approval;
  if (
    approval === 'auto_approve' ||
    approval === 'require_approval' ||
    approval === 'forbidden'
  ) {
    patch.policy = approval;
  }
  const sideEffects = parseHelperSideEffects(args.sideEffects);
  if (sideEffects) patch.sideEffects = sideEffects;
  return patch;
}

/**
 * Apply one Tool Helper mutation tool call to the in-form draft.
 *
 * #1092 — Pre-fix the Accept button on the Tool Helper card invoked
 * `chat.consumeMutation(m.callId)` only — the route's HelperPanel
 * was mounted WITHOUT an `onApplyMutation` callback, so the proposal
 * card silently disappeared and none of the drafted metadata landed
 * on the form. The form stayed entirely blank and "Create Tool"
 * remained disabled. The helper appeared to work but produced zero
 * end-user value.
 *
 * The fix mirrors the PR #1159 pattern used for Prompt Helper / Skill
 * Helper Accept: extract a pure helper that turns one
 * `{ toolName, args }` pair into a `Partial<ToolHelperDraft>` patch.
 * The route then applies the patch via a single functional setState
 * per field, so multi-call batches (e.g. `set_metadata` followed by
 * `set_source` + `set_policy` in the same Accept-All click) stack
 * atomically instead of clobbering each other through stale React
 * closures.
 *
 * The Tool Helper backend emits 7 mutation tools (see
 * `packages/ai-core/src/helper-mutation-tools.ts` → `toolMutationTools`):
 *   - `set_metadata`     → name / displayName / description on the form
 *   - `set_http_manifest`→ complete HTTP source/config/schema/policy fields
 *   - `set_source`       → toolType (http / database) + httpUrl
 *   - `set_input_schema` → parameter rows (JSON Schema → table)
 *   - `set_output_schema`→ output schema JSON textarea on the create
 *                          form; persisted as workspace_tools.spec.output.
 *   - `set_policy`       → policy + side-effect markers
 *   - `add_valve`        → required env vars; v1 form has no valve
 *                          editor, so this is a no-op patch
 *
 * Unknown tool names → empty patch (no-op).
 *
 * @param toolName  - mutation tool name the helper emitted
 * @param args      - the helper's tool args (validated server-side)
 * @returns a partial patch over `ToolHelperDraft`
 */
export function applyToolHelperMutation(
  toolName: string,
  args: Record<string, unknown>,
): Partial<ToolHelperDraft> {
  switch (toolName) {
    case 'set_metadata': {
      const patch: Partial<ToolHelperDraft> = {};
      // The helper may emit either `slug` (canonical) or `name`. Both
      // map to the form's `name` field (which becomes the URL slug).
      // The form's onChange strips non-[a-z0-9_]; we run the same
      // sanitiser so e.g. "weather.get_current" lands as
      // "weather_get_current".
      if (args.slug != null) patch.name = sanitizeToolSlug(String(args.slug));
      else if (args.name != null) patch.name = sanitizeToolSlug(String(args.name));
      // `name` (the helper-side label) is the form's `displayName`.
      // Keep the original casing — `displayName` is free-text.
      if (args.name != null) patch.displayName = String(args.name);
      if (args.description != null) patch.description = String(args.description);
      return patch;
    }
    case 'set_http_manifest': {
      return applyHttpManifestArgs(args);
    }
    case 'set_source': {
      const patch: Partial<ToolHelperDraft> = {};
      const src = args.sourceType;
      // The tenant v1 form supports `http` and `database` only. MCP /
      // function / openapi are routed elsewhere (see #148); treat
      // those as a no-op so the helper doesn't silently break the
      // form. The user gets the helper card's prose narration as
      // explanation; clicking Accept consumes the card without
      // changing the form mode.
      if (src === 'http_rest') {
        Object.assign(patch, applyHttpManifestArgs(args));
        if (args.executorRef != null) patch.httpUrl = String(args.executorRef);
      } else if (src === 'database') {
        patch.toolType = 'database';
        if (args.executorRef != null) patch.dbConnection = String(args.executorRef);
      }
      return patch;
    }
    case 'set_input_schema': {
      const parsed = parseToolInputSchemaForHelper(args.schemaJson);
      if (parsed && parsed.length > 0) return { parameters: parsed };
      return {};
    }
    case 'set_policy': {
      const patch: Partial<ToolHelperDraft> = {};
      const approval = args.approval;
      if (
        approval === 'auto_approve' ||
        approval === 'require_approval' ||
        approval === 'forbidden'
      ) {
        patch.policy = approval;
      }
      const sideEffects = parseHelperSideEffects(args.sideEffects);
      if (sideEffects) patch.sideEffects = sideEffects;
      return patch;
    }
    // No-op kinds — see header comment for rationale. Returning an
    // empty patch makes the Accept click consume the card without
    // touching the form, which matches the user's intuition that the
    // proposal was "acknowledged".
    case 'set_output_schema': {
      const schemaJson = args.schemaJson ?? args.outputSchemaJson;
      if (schemaJson != null) return { outputSchemaJson: String(schemaJson) };
      return {};
    }
    case 'add_valve':
      return {};
    default:
      return {};
  }
}

// ─── JSON parsing ────────────────────────────────────────────────────────────

/**
 * Result envelope used by all JSON helpers below. Discriminated union
 * — never throws to the caller.
 */
export type JsonParseResult<T> =
  | { ok: true; value: T }
  | { ok: false; err: string };

/**
 * Parse a JSON text blob. Returns a discriminated union with a
 * caller-friendly error message that names the offending field.
 *
 * Previously inlined three different ways:
 *   - tenant detail (`studio/tools.$toolId.tsx`) had a `parseJson(text, label)`
 *     inner helper used 3x in `handleSave`.
 *   - sysadmin studio scaffolding (`sysadmin/studio/tools.tsx`) had 3 separate
 *     `try { JSON.parse(...) } catch { ... }` blocks inside `validateAndBuild`.
 *   - sysadmin catalog list + detail (`catalog.tools.tsx`, `catalog.tools.$id.tsx`)
 *     each had 4 separate `try / catch` blocks with `toast.error` baked in.
 *
 * This helper covers the parsing concern only. The caller decides
 * whether to surface the error via `toast.error` (sysadmin pages),
 * `setFormError(...)` (tenant pages), or just propagate.
 *
 * @param text the JSON blob (typically a `<Textarea>` value)
 * @param label the user-facing field name, e.g. "Handler config"
 */
export function parseJsonTyped(
  text: string,
  label: string,
): JsonParseResult<unknown> {
  // Preserve the tenant detail behaviour: empty text was never a
  // valid input there, but the sysadmin pages defaulted to '{}'
  // before calling. Don't second-guess — just parse what we got.
  // Callers that need an empty-default should pass '{}' / '[]'.
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch (e) {
    return {
      ok: false,
      err: `${label} is not valid JSON: ${
        e instanceof Error ? e.message : 'parse error'
      }`,
    };
  }
}

/**
 * Parse a JSON text blob and confirm the result is an array of
 * strings (the shape `tool.sideEffects` must satisfy). Two of the
 * sysadmin files had inline copies of this exact pattern — see
 * `sysadmin/studio/tools.tsx` ~L140, `sysadmin/catalog.tools.tsx` ~L267,
 * `sysadmin/catalog.tools.$id.tsx` ~L1135 for the originals.
 */
export function tryParseJsonArrayOfStrings(
  text: string,
  label: string,
): JsonParseResult<string[]> {
  const parsed = parseJsonTyped(text, label);
  if (!parsed.ok) return parsed;
  if (!Array.isArray(parsed.value)) {
    return { ok: false, err: `${label} must be a JSON array of strings.` };
  }
  if (!parsed.value.every((v) => typeof v === 'string')) {
    return { ok: false, err: `${label} must be a JSON array of strings.` };
  }
  // Cast through unknown because we just runtime-validated every item.
  return { ok: true, value: parsed.value as string[] };
}

// ─── Slug validation ─────────────────────────────────────────────────────────

/**
 * Result envelope for slug validators.
 */
export type SlugValidationResult =
  | { ok: true; slug: string }
  | { ok: false; err: string };

/**
 * Tenant-side slug rules: lowercase alphanumeric + underscore ONLY.
 * No dots, no dashes. The namespace prefix (`custom.` / `db.`) is
 * applied by the API, not the user, so the user-typed slug is the
 * unqualified part.
 *
 * Previously inlined in `studio/tools.new.tsx` as an inline
 * `e.target.value.replace(/[^a-z0-9_]/g, '')` on the `<Input>`
 * onChange. Extracted as a validator (rather than a sanitiser) so
 * the caller can choose to either:
 *   a) sanitise on keystroke (existing tenant UX), or
 *   b) validate on submit (potential future UX).
 *
 * Empty slugs are rejected — `canSave` in the tenant page already
 * gates on `name.trim()`, but defensive callers can rely on this.
 */
export function validateSlugTenant(slug: string): SlugValidationResult {
  const trimmed = slug.trim();
  if (!trimmed) {
    return { ok: false, err: 'Slug is required.' };
  }
  if (!/^[a-z0-9_]+$/.test(trimmed)) {
    return {
      ok: false,
      err:
        'Slug must be lowercase alphanumeric with underscores only (no dots, dashes, or spaces).',
    };
  }
  return { ok: true, slug: trimmed };
}

/**
 * Sysadmin-side slug rules: lowercase alphanumeric + underscore +
 * dot + dash. Sysadmin tools use dotted namespaces like
 * `system.lookup_vendor` or `iq.search-asset`, so the slug is the
 * full identifier.
 *
 * Previously expressed two different ways:
 *   - `sysadmin/studio/tools.tsx` validated with `/^[a-z0-9_.-]+$/`
 *     on submit.
 *   - `sysadmin/catalog.tools.tsx` sanitised with
 *     `.toLowerCase().replace(/[^a-z0-9_.-]/g, '-')` on keystroke.
 *
 * The CHARACTER CLASS matches (both allow `[a-z0-9_.-]`); only the
 * UX differs. We provide the validator here and let each caller
 * keep their existing onChange behaviour. Callers that want
 * keystroke-sanitisation can compose `slug.toLowerCase().replace(...)`
 * before passing to this validator.
 */
export function validateSlugSysadmin(slug: string): SlugValidationResult {
  const trimmed = slug.trim();
  if (!trimmed) {
    return { ok: false, err: 'Slug is required.' };
  }
  if (!/^[a-z0-9_.-]+$/.test(trimmed)) {
    return {
      ok: false,
      err:
        'Slug must be lowercase alphanumeric with dots, dashes, or underscores only.',
    };
  }
  return { ok: true, slug: trimmed };
}

// ─── URL validation (tenant-only — kept here for import discipline) ──────────

/**
 * Validate an HTTP/HTTPS URL that MAY contain `{{templateVariable}}`
 * placeholders. The validator substitutes `__probe__` for every
 * `{{...}}` occurrence, then runs Zod's URL parser. This is the
 * exact pattern that's in production today in `studio/tools.new.tsx`
 * (Round 5 fix #259); the server runs the same Zod check for defense
 * in depth.
 *
 * Tenant-only at the moment — sysadmin catalogs don't expose a URL
 * input field. Extracted here anyway so the import discipline
 * (one place to find tool-shared helpers) holds.
 *
 * Returns `null` on success (the existing call site treats the
 * helper's result as `string | null` for direct binding to
 * `aria-describedby`); returns the user-facing error string on
 * failure.
 *
 * @param url the URL string (`{{var}}` placeholders allowed)
 * @returns null if valid, error message otherwise
 */
export function validateUrlWithTemplates(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) {
    // Empty is "no opinion" — let the caller's required-field gate
    // handle empty. The previous inline implementation in
    // `studio/tools.new.tsx` returned null for empty so we match.
    return null;
  }
  const probe = trimmed.replace(/\{\{[^}]+\}\}/g, '__probe__');
  return z.string().url().safeParse(probe).success
    ? null
    : 'Enter a fully qualified URL such as "https://api.example.com/v1/resource". `{{variable}}` placeholders are allowed.';
}
